import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authService } from '../services/authService';

/**
 * Google OAuth return page: exchanges one-time code for session tokens.
 */
export default function AuthCallback({ onLoginSuccess }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const processed = useRef(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('Completing sign-in...');

  useEffect(() => {
    const code = searchParams.get('code');
    if (!code) {
      setError('Missing authentication code.');
      return;
    }

    // Survive React StrictMode remount (one-time codes can only be used once)
    const lockKey = `oauth_exchange_${code}`;
    if (processed.current || sessionStorage.getItem(lockKey) === 'done') {
      return;
    }
    if (sessionStorage.getItem(lockKey) === 'pending') {
      return;
    }

    processed.current = true;
    sessionStorage.setItem(lockKey, 'pending');

    const run = async () => {
      try {
        const result = await authService.exchangeOAuthCode(code);
        if (result.success) {
          sessionStorage.setItem(lockKey, 'done');
          window.history.replaceState({}, document.title, '/auth/callback');
          setStatus('Signed in. Redirecting...');
          if (onLoginSuccess) {
            await onLoginSuccess(result.user);
          } else if (result.user?.isAdmin) {
            navigate('/admin', { replace: true });
          } else {
            navigate('/my-programs', { replace: true });
          }
        } else {
          sessionStorage.removeItem(lockKey);
          setError(result.message || 'Authentication failed');
        }
      } catch (err) {
        console.error('OAuth exchange failed:', err);
        sessionStorage.removeItem(lockKey);
        setError(err.response?.data?.message || err.message || 'Authentication failed');
      }
    };

    run();
  }, [searchParams, navigate, onLoginSuccess]);

  if (error) {
    return (
      <div style={{ padding: 40, textAlign: 'center', minHeight: '50vh' }}>
        <h3>Sign-in failed</h3>
        <p>{error}</p>
        <button type="button" onClick={() => navigate('/')}>
          Go home
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: 40, textAlign: 'center', minHeight: '50vh' }}>
      <h3>{status}</h3>
    </div>
  );
}
